import { Link } from 'react-router-dom';

import { ThemeToggle } from 'src/components/ui/ThemeToggle';
import { OUTPUT_STATUS_LABEL } from 'src/lib/reader';
import { formatDate } from 'src/lib/time';
import type { EditorialOutput, EditorialStream } from 'src/types/editorial';

const STATE_TONE: Record<string, string> = {
  pending_human_review: 'ok',
  validation_failed: 'bad',
  blocked_eligibility: 'warn',
  generation_failed: 'bad',
};

type ReaderShellProps = {
  streamId: string;
  output: EditorialOutput | null;
  stream: EditorialStream | null;
  comparing: boolean;
  children: React.ReactNode;
};

/**
 * The reader is a deliberate escape from the review chrome: no stream bar, no stage nav, its own
 * masthead. An editor reading a draft as a reader would is doing a different job from an editor
 * auditing it, and the two must not look alike.
 *
 * The masthead is set the way a front page is set — an edition line above the wordmark, a
 * hairline-and-rule pair below it, the broadcast named as the edition's source. It is furniture
 * with a job: it tells the reader, before they read a word, that they are looking at the piece
 * rather than at the machine that made it. The utility bar above it is the way back, and it is
 * the only review chrome the page keeps.
 */
export function ReaderShell({ streamId, output, stream, comparing, children }: ReaderShellProps) {
  const status = output?.payload.status ?? '';
  const edition = output?.payload.generated_at ?? stream?.createdAt ?? '';

  return (
    <div className="reader">
      <div className="r-utility">
        <Link to={`/stream/${encodeURIComponent(streamId)}/notes`} data-nav>
          ← Panel de revisión
        </Link>
        {output &&
          (comparing ? (
            <Link to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(output.transformationId)}`}>
              Leer la nota
            </Link>
          ) : (
            <Link
              to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(output.transformationId)}/comparar`}
            >
              Comparar versiones
            </Link>
          ))}
        <span className="r-id mono">{streamId}</span>
        {output && (
          <span className="r-state" data-tone={STATE_TONE[status] ?? 'warn'}>
            {OUTPUT_STATUS_LABEL[status] ?? status}
          </span>
        )}
        {/* Reading a long piece at night is exactly when this is wanted, and the reader was the
            one screen that could not reach it. */}
        <ThemeToggle />
      </div>

      <div className="r-page">
        <header className="r-masthead">
          <p className="r-edition">
            <span>Edición editorial</span>
            <span className="r-edition-mid">{edition ? formatDate(edition) : 'Sin fecha'}</span>
            <span>Redacción automatizada</span>
          </p>
          <h1 className="r-wordmark">Pressentia</h1>
          <p className="r-masthead-source">
            {stream?.title || 'Transmisión'}
            {stream?.sourceUrl && (
              <>
                {' · '}
                <a href={stream.sourceUrl} target="_blank" rel="noreferrer noopener">
                  ver la transmisión
                </a>
              </>
            )}
          </p>
        </header>
        {children}
      </div>
    </div>
  );
}
